<template>
  <fullscreen-overlay-frame :title="$t('location-dialog.title').toString()"
                            icon="fmd_good"
                            color="primary"
                            centered
                            closable
                            maximizable
                            @close="close"
                            @maximize="maximize"
                            @minimize="minimize">
    <template v-slot:content>
      <div v-html="$t('location-dialog.description')"/>
      <v-card>
        <v-card-text>
          <!-- v-form for Location
         Inputs: Street, housenumber, zip, City, Country (Dropdown)-->
          <v-form ref="locationForm" v-model="valid" class="px-2 pt-6">
            <!-- Street -->
            <v-text-field
                outlined
                v-model="location.street"
                :label="$t('location-dialog.street').toString()"
                required
                :error-messages="streetErrors"
                :rules="rules"
            />
            <!-- Housenumber -->
            <v-text-field
                outlined
                v-model="location.housenumber"
                :label="$t('location-dialog.housenumber').toString()"
                required
                :error-messages="housenumberErrors"
                :rules="rules"
            />
            <!-- Zip -->
            <v-text-field
                outlined
                v-model="location.zip"
                :label="$t('location-dialog.zip').toString()"
                required
                :error-messages="zipErrors"
                :rules="rules"
            />
            <!-- City -->
            <v-text-field
                outlined
                v-model="location.city"
                :label="$t('location-dialog.city').toString()"
                required
                :error-messages="cityErrors"
                :rules="rules"
            />
            <!-- Country -->
            <v-select
                outlined
                v-model="location.country"
                :items="countries"
                :label="$t('location-dialog.country').toString()"
                required
                :error-messages="countryErrors"
                :rules="rules"
            />
            <!-- Use site Address button -->
            <v-btn v-show="siteLocation.valid && !editing" @click="useSiteAddress" :disabled="submitting" block
                   large depressed color="primary">
              {{ $t('location-dialog.use-site-address') }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </template>
    <template v-slot:actions>

      <!-- save button -->
      <v-btn v-if="!editing"
             :disabled="submitting || !valid"
             :loading="submitting"
             large depressed
             color="primary"
             class="font-weight-bold action-button"
             @click="submit">
        <v-icon left>add</v-icon>
        {{ $t('location-dialog.save') }}
      </v-btn>

      <!-- update button -->
      <v-btn v-if="editing"
             :disabled="submitting || !valid"
             :loading="submitting"
             large depressed
             color="primary"
             class="font-weight-bold action-button"
             @click="submit">
        <v-icon left>update</v-icon>
        {{ $t('location-dialog.update') }}
      </v-btn>

      <!-- delete button -->
      <v-btn v-if="editing"
             :disabled="submitting"
             large outlined depressed
             color="primary"
             class="mt-4 ml-0 font-weight-bold action-button"
             @click="deleteLocation">
        <v-icon left>delete</v-icon>
        {{ $t('location-dialog.delete') }}
      </v-btn>
    </template>
  </fullscreen-overlay-frame>
</template>

<script>
import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame.vue";
import {createEnodeLib} from "@/plugins/enodePlugin";

export default {
  name: "LocationDialog",
  components: {FullscreenOverlayFrame},
  props: ['data'],
  data() {
    return {
      location: {
        street: '',
        housenumber: '',
        zip: '',
        city: '',
        country: 'Deutschland'
      },
      siteLocation: {
        street: '',
        housenumber: '',
        zip: '',
        city: '',
        country: '',
        valid: false
      },
      valid: true,
      // Rules for form validation
      rules: [v => !!v || this.$t("location-dialog.required").toString()],
      streetErrors: [],
      housenumberErrors: [],
      zipErrors: [],
      cityErrors: [],
      countryErrors: [],
      countries: ['Deutschland'],
      submitting: false,
      enodePlugin: createEnodeLib()
    }
  },

  methods: {
    submit() {
      this.submitting = true
      // Validate form
      this.$refs.locationForm.validate()
      if (!this.valid) {
        this.submitting = false
        return
      }
      this.enodePlugin.createOrUpdateLocation(this.location, !this.editing).then(() => {
        this.$root.bisatoast.success({
          message: this.$t('location-dialog.location-creation.success'),
          showCloseBtn: true
        })
        this.submitting = false
        this.close()
      }).catch((error) => {
        console.error('Error creating or updating location:', error)
        if (error.nominatimError) {
          if (error.nominatimCheck.address) {
            this.$root.bisadialog.toggle('confirmation', true, {
              text: this.$t('location-dialog.address-no-match') + ' ' + error.nominatimCheck.address,
              confirmLabel: this.$t('location-dialog.confirm-address'),
            })
            this.$root.$on('dialogConfirmed', () => {
              this.$root.$off('dialogConfirmed')
              this.$root.$off('dialogAborted')
              // Put correct information into the location object
              this.location.street = error.response.data[0].address.road
              this.location.housenumber = error.response.data[0].address.house_number
              this.location.zip = error.response.data[0].address.postcode
              this.location.city = this.getCityFromAddress(error.response.data[0].address)
              this.location.country = error.response.data[0].address.country
              this.submit()
            })
            this.$root.$on('dialogAborted', () => {
              this.$root.$off('dialogConfirmed')
              this.$root.$off('dialogAborted')
            })
          } else {
            console.error('Error getting coordinates:', error)
            this.$root.bisatoast.error({
              message: this.$t('location-dialog.geocoding.no-results'),
              showCloseBtn: true
            })

          }
        } else {
          console.error('Error creating or updating location:', error)
          this.$root.bisatoast.error({
            message: this.$t('app.generic-error'),
            showCloseBtn: true
          })
        }
        this.submitting = false
      })
    },
    deleteLocation() {
      this.enodePlugin.deleteLocation(this.data.location.id).then(() => {
        this.$root.bisatoast.success({
          message: this.$t('location-dialog.location-deletion.success'),
          showCloseBtn: true
        })
        this.close()
      }).catch(() => {
        this.$root.bisatoast.error({
          message: this.$t('app.generic-error'),
          showCloseBtn: true
        })
      })
    },
    /**
     * maximize dialog
     */
    maximize() {
      this.fullscreen = true
      this.$emit('maximize')
    },

    /**
     * minimize dialog
     */
    minimize() {
      this.fullscreen = false
      this.$emit('minimize')
    },

    /**
     * close dialog
     */
    close() {
      this.$root.bisadialog.toggle('location')
      this.$root.bisadialog.toggle(this.data?.originDialog, true, this.data?.originData)
      this.$root.bisadialog.callDialogInit(this.data?.originDialog)
    },
    init() {
      // Get address components from name
      let addressComponents = this.data.location.name.split(', ')
      let place = addressComponents[1].split(' ')
      this.location.zip = place[0]
      this.location.city = place.slice(1, place.length).join(' ')
      this.location.country = addressComponents[2]
      let streetAndHousenumber = addressComponents[0].split(' ')
      // street may ' ' so use last element as housenumber, all other elements as street
      this.location.street = streetAndHousenumber.slice(0, streetAndHousenumber.length - 1).join(' ')
      this.location.housenumber = streetAndHousenumber[streetAndHousenumber.length - 1]
      this.location.id = this.data.location.id
    },
    getAddress() {
      this.$rhRequest.sendGet({
        endpoint: "site/get-general-information",
      }, (response) => {
        let city = response?.data?.data?.city
        let zip = response?.data?.data?.zip
        let street = response?.data?.data?.street
        // Call Geocoding API (Nominatim) to get coordinates
        // Build Url for Geocoding API
        let host = 'https://nominatim.openstreetmap.org/'
        let endpoint = 'search?q='
        let query = street + ', ' + zip + ' ' + city
        let url = endpoint + query + '&format=jsonv2&addressdetails=1&accept-language=de'
        // Make axios request
        this.$rhRequest.sendGet(
            {
              host: host,
              endpoint: url
            },
            (response) => {
              if (!!response?.data && response?.data?.length > 0) {
                let address = response?.data[0].address
                if (address.country === 'Deutschland') {
                  this.siteLocation.street = address.road
                  this.siteLocation.housenumber = address.house_number
                  this.siteLocation.zip = address.postcode
                  this.siteLocation.city = this.getCityFromAddress(address)
                  this.siteLocation.country = address.country
                  this.siteLocation.valid = true
                }
              }
            },
            (error) => {
              console.error('Error getting coordinates:', error)
              this.$root.bisatoast.error({
                message: this.$t('app.generic-error'),
                showCloseBtn: true
              })
            }
        )
      }, (error) => {
        console.error(error)
      })
    },
    useSiteAddress() {
      this.location.street = this.siteLocation.street
      this.location.housenumber = this.siteLocation.housenumber
      this.location.zip = this.siteLocation.zip
      this.location.city = this.siteLocation.city
    },
    getCityFromAddress(address) {
      if (address.city) {
        return address.city
      } else if (address.town) {
        return address.town
      } else if (address.village) {
        return address.village
      } else if (address.municipality) {
        return address.municipality
      }
    }
  },
  mounted() {
    this.getAddress()
  },
  computed: {
    editing() {
      return !!this.data.location
    }
  }
}
</script>